import api, { parseErrorMessage } from 'api'
import { isAxiosError } from 'axios'
import { format } from 'date-fns'

export type UploadVmsTimesheetRequest = {
  companyId: number
  file: File
  dryRun: boolean
}

export type UploadVmsTimesheetResponse = {
  blob: Blob
  filename: string
}

export const uploadVmsTimesheet = async ({
  companyId,
  file,
  dryRun,
}: UploadVmsTimesheetRequest): Promise<UploadVmsTimesheetResponse> => {
  const formData = new FormData()
  formData.append('timesheet', file)

  try {
    const response = await api.post<Blob>(
      `/admin/vms/timesheet_upload/${companyId}`,
      formData,
      {
        params: { dry_run: dryRun },
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: 'text/csv',
        },
        responseType: 'blob',
      }
    )
    const suffix = dryRun ? '_dry_run' : ''
    const timestamp = format(new Date(), 'yyyy-MM-dd_HHmm')
    const filename = `vms_timesheet_results${suffix}_${timestamp}.csv`
    return { blob: response.data, filename }
  } catch (err) {
    throw new Error(await parseUploadError(err))
  }
}

// responseType is 'blob', so axios wraps the JSON error body as a Blob. Decode
// it before parsing. The endpoint returns endpoint-specific detail (e.g.
// duplicate row indices) instead of a marshmallow-shaped { message } body, so
// fall through to parseErrorMessage only when the shape matches; otherwise
// show the full body so the operator sees all the detail.
const parseUploadError = async (err: unknown): Promise<string> => {
  if (isAxiosError(err) && err.response?.data instanceof Blob) {
    const text = await err.response.data.text()
    let parsed: unknown
    try {
      parsed = JSON.parse(text)
    } catch {
      return text || err.message
    }
    if (parsed && typeof parsed === 'object' && 'message' in parsed) {
      err.response.data = parsed
      return parseErrorMessage(err) ?? JSON.stringify(parsed, null, 2)
    }
    return JSON.stringify(parsed, null, 2)
  }
  return parseErrorMessage(err) ?? 'Upload failed'
}
